import Box from "@mui/material/Box";
import { Suspense, lazy, useEffect } from "react";
import { Outlet } from "react-router-dom";

import PageFallback from "@/components/Core/PageFallback";
import {
  useDeviceType,
  useGameState,
  useIsLoggedIn,
  useScrollToTopOnRouteChange,
  useShowFooter,
} from "@/hooks";
import { BootShellExit } from "@/lib/bootShell";
import { getFavoriteGameIds } from "@/services";
import { ChatRoom } from "@/views/Profile/ChatRoom";

import Footer from "./Footer";
import { MobileFooter } from "./Footer/MobileFooter";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { MainContent } from "./styles";

interface LayoutProps {
  hideFooter?: boolean;
}

// Lazy: the slip pulls in the sportsbook text and odds helpers; it renders once those arrive.
const BetSlip = lazy(() => import("./BetSlip"));

const Layout = ({ hideFooter = false }: LayoutProps) => {
  const { isMobile } = useDeviceType();
  const isLoggedIn = useIsLoggedIn();
  const { setFavorites } = useGameState();

  const showFooter = useShowFooter();
  const scrollRef = useScrollToTopOnRouteChange();

  useEffect(() => {
    if (!isLoggedIn) {
      setFavorites([]);
      return;
    }
    let active = true;
    getFavoriteGameIds()
      .then((ids) => {
        if (active) setFavorites(ids);
      })
      .catch((err) => console.error("Failed to sync favorites:", err));
    return () => {
      active = false;
    };
  }, [isLoggedIn]);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar />
      <Header />

      <MainContent id="main-content" ref={scrollRef} isMobile={isMobile}>
        <Box component="main" sx={{ minHeight: "calc(100vh - 68px)" }}>
          <Suspense fallback={<PageFallback />}>
            <BootShellExit />
            <Outlet />
          </Suspense>
        </Box>
        {!hideFooter && showFooter && <Footer />}
      </MainContent>

      <Suspense fallback={null}>
        <BetSlip />
      </Suspense>
      {showFooter && isMobile && <MobileFooter />}
      <ChatRoom />
    </Box>
  );
};

export default Layout;
